@use '../../styles/tokens' as *;
@use '../../styles/utilities' as util;

// Shared parts for the map sidebars (Lookout, Property details). The pattern:
// white fixed bars (header, toolbar, action bar) wrapping an off-white
// scrolling body, separated by hairlines.

// $paddingRelaxed is the panel's gutter: every row of the panel — header,
// section, toolbar, action bar — uses it, so headings and controls at the same
// level share a left edge.

// Panel ────────────────────────────────────────────────────

// The opaque surface the sidebar puts between its content and the map.
.panel {
  background-color: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.panelBar {
  flex: none;
  // Above the body, so the bar's shadow falls on the content it pins.
  position: relative;
  z-index: $z1;
}

// A shadow separates a fixed bar from the scrolling body, not a rule: the two
// often share a background, and a hairline can't tell "different surface" from
// "next item in the list". In-body separators stay hairlines. The header and
// toolbar share one bar so the body scrolls under a single shadow.
.panelBarTop {
  box-shadow: $shadowSurface;
}

// $shadowSurface pointing up, for a bar the body scrolls under from below.
.panelBarBottom {
  box-shadow:
    0 -1px 2px rgba(0, 0, 0, 0.08),
    0 -4px 18px rgba(0, 0, 0, 0.04);
}

.panelBody {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.panelBodyWhite {
  background-color: white;
}

.panelBodyOffWhite {
  background-color: $colorOffWhite;
}

// Header ───────────────────────────────────────────────────

.header {
  align-items: flex-start;
  background-color: $colorOffWhite;
  display: flex;
  gap: $gapSnug;
  padding: $paddingRelaxed;
}

.headerText {
  flex: 1;
  min-width: 0;
}

// Color and size only: the h3 carries the weight and line height.
.headerTitle {
  color: $colorPrimary;
  font-size: $appNavFontSize;
  word-break: break-word;
}

.headerSubtitle {
  color: $colorDarkestGray;
  font-size: $smallFontSize;
  margin-top: $space0_25;
}

// Edit, copy, and info buttons. Hidden at rest so a column of values reads
// cleanly; revealed on hover or focus so they stay reachable by keyboard and
// touch.
.hoverActions,
.labelAction {
  @include util.flexRow;
  flex: none;
  gap: $gapExtraSnug;
  opacity: 0;
  transition: opacity 100ms ease;
}

.header:hover .hoverActions,
.header:focus-within .hoverActions,
.fieldRow:hover .hoverActions,
.fieldRow:focus-within .hoverActions,
.fieldRow:hover .labelAction,
.fieldRow:focus-within .labelAction,
// A menu opened from one of these buttons moves focus into its portal, which
// would otherwise fade the button out while its menu is up.
.fieldRow:has(:global(.bp5-popover-open)) .hoverActions {
  opacity: 1;
}

// No hover on touch, so the controls have to stay visible there.
@media (hover: none) {
  .hoverActions,
  .labelAction,
  .sectionRightElementHoverOnly {
    opacity: 1;
  }
}

// Section (accordion) ──────────────────────────────────────

.section {
  &.sectionBordered {
    border-top: 1px solid $colorLightGray;
  }

  &.sectionCard {
    background-color: $colorOffWhite;
    border: 1px solid $colorLightGray;
    border-radius: $borderRadius;
    overflow: hidden;
  }
}

.sectionBordered {
}
.sectionCard {
}

.sectionHeaderRow {
  align-items: center;
  display: flex;

  &:hover {
    background-color: $colorLightestGray;
  }
}

.sectionHeader {
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex: 1;
  font-family: inherit;
  gap: $gapDefault;
  min-width: 0;
  padding: $paddingRelaxed;
  text-align: left;

  .sectionCard & {
    padding: $paddingDefault;
  }
}

.sectionChevron {
  @include util.flexRow;
  color: $colorDarkGray;
  flex: none;

  svg {
    transition: transform 200ms ease;
  }
}

.sectionChevronClosed svg {
  transform: rotate(-90deg);
}

.sectionTitle {
  color: $colorOffBlack;
  font-size: $appNavFontSize;
  font-weight: $boldFontWeight;

  // A card sits inside another section's body, so its title drops a step.
  .sectionCard & {
    font-size: $paragraphFontSize;
  }
}

.sectionMeta {
  @include util.truncate;
  color: $colorDarkestGray;
  font-size: $smallFontSize;
  min-width: 0;
}

.sectionRightElement {
  @include util.flexRow;
  flex: none;
  gap: $gapSnug;
  padding-right: $paddingRelaxed;

  .sectionCard & {
    padding-right: $paddingDefault;
  }
}

.sectionRightElementHoverOnly {
  opacity: 0;
  transition: opacity 100ms ease;
}

.sectionHeaderRow:hover .sectionRightElementHoverOnly,
.sectionHeaderRow:focus-within .sectionRightElementHoverOnly {
  opacity: 1;
}

.sectionBody {
  padding: 0 $paddingRelaxed $paddingRelaxed;

  .sectionCard & {
    padding: 0 $paddingDefault $paddingDefault;
  }
}

// Toolbar ──────────────────────────────────────────────────

.toolbar {
  align-items: center;
  background-color: $colorOffWhite;
  display: flex;
  gap: $gapDefault;
  padding: $paddingDefault $paddingRelaxed;
}

// The leading control stands in the same column as a section's chevron, so both
// headings start at the same x. Blueprint reserves space around a control for a
// label — wider than the 16px indicator it draws, and with a margin below it.
// There's no label here, so that space only pushes the heading right and lifts
// the control off the row's center line.
.toolbarLeading {
  @include util.flexRow;
  flex: none;

  // Qualified the way Blueprint writes the rules these undo, so they win on
  // specificity rather than on which stylesheet the bundler emits last.
  :global(.bp5-control:not(.bp5-align-right)) {
    @include util.labelLessBlueprintControl;
  }
}

// Matches .sectionTitle, so a toolbar heading and a section heading in the same
// panel read as the same level.
.toolbarLabel {
  align-items: center;
  color: $colorOffBlack;
  display: inline-flex;
  font-size: $appNavFontSize;
  font-weight: $boldFontWeight;
  gap: $gapDefault;
}

.toolbarActions {
  @include util.flexRow;
  gap: $gapExtraSnug;
  margin-left: auto;
}

// Action bar ───────────────────────────────────────────────

.actionBar {
  background-color: white;
  padding: $paddingDefault $paddingRelaxed;
}

.actionBarContext {
  @include util.flexRow;
  gap: $gapSnug;
}

.actionBarContextLabel {
  @include util.truncate;
  color: $colorOffBlack;
  font-size: $compactFontSize;
  font-weight: $boldFontWeight;
  min-width: 0;
}

.actionBarContextAction {
  flex: none;
  margin-left: auto;
}

.actionBarButtons {
  display: flex;
  gap: $gapSnug;
  margin-top: $marginBlockDefault;

  // The first action is the primary one and fills the remaining width.
  > *:first-child {
    flex: 1;
  }
}

.actionBarShortcuts {
  color: $colorDarkGray;
  font-size: $compactFontSize;
  margin-top: $marginBlockSnug;
}

.actionBarHint {
  background-color: white;
  color: $colorLightSlate;
  font-size: $compactFontSize;
  padding: $paddingRelaxed;
  text-align: center;
}

// Card ─────────────────────────────────────────────────────

// Overrides B.Card's padding, radius and elevation shadow. Qualified with the
// Blueprint class so these win on specificity — bundled module CSS is not
// reliably emitted after blueprint.css. B.Card's white background carries over.
.card:global(.bp5-card) {
  border: 1px solid $colorLightGray;
  border-radius: $borderRadius;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: $paddingDefault;

  &:global(.bp5-interactive):hover {
    box-shadow: $shadowHover;
  }

  // Open on the map. Matches the glow on a focused note card, and stops there:
  // adding a blue border on top of the glow reads as a hard outline.
  &.cardFocused {
    box-shadow: 0 0 0.4rem $colorBrightBlue;

    &:hover {
      box-shadow: 0 0 0.6rem $colorBrightBlue;
    }
  }
}

.cardFocused {
}

.cardHeader {
  align-items: center;
  display: flex;
  gap: $gapSnug;
  margin-bottom: $space0_75;
}

.cardList {
  display: flex;
  flex-direction: column;
  gap: $gapDefault;
}

// Field row ────────────────────────────────────────────────

.fieldGroup {
  display: flex;
  flex-direction: column;
  gap: $gapDefault;
}

.fieldRow {
  column-gap: $space0_75;
  display: flex;
  row-gap: $space0_25;

  &.fieldRowAuto {
    align-items: center;
  }

  // Flex line breaking measures each item's natural width, so a value that
  // can't sit beside the label wraps to its own line and fills it. Because the
  // decision is made on natural width, the extra room the value gains down
  // there can't send it back up — no oscillation.
  &.fieldRowAuto {
    flex-wrap: wrap;
  }

  &.fieldRowStacked {
    flex-direction: column;
  }
}

.fieldRowAuto {
}
.fieldRowStacked {
}

.fieldLabel {
  @include util.flexRow;
  color: $colorDarkestGray;
  font-size: $smallFontSize;
  font-weight: $boldFontWeight;
  gap: $gapSnug;
  letter-spacing: 0.03em;
  text-transform: uppercase;

  // A label that outgrows the rail widens its own row rather than wrapping over
  // two lines. That pushes the row's value past the rail too, which makes the
  // value the more likely one to wrap onto its own line — where it belongs.
  .fieldRowAuto > & {
    flex: none;
    min-width: var(--sidebarFieldLabelWidth, 10rem);
    white-space: nowrap;
  }
}

.fieldValueWrapper {
  align-items: center;
  display: flex;
  flex: 1;
  gap: $space0_75;
  min-width: 0;

  // The natural width the wrap decision is made on. `flex-grow` still fills
  // whichever line the value lands on, and `min-width: 0` lets it shrink to fit.
  .fieldRowAuto > & {
    flex-basis: max-content;
  }
}

.fieldValue {
  color: $colorOffBlack;
  flex: 1;
  font-size: $paragraphFontSize;
  min-width: 0;

  &.fieldValueTruncated {
    @include util.truncate;
  }
}

.fieldValueTruncated {
}

.emptyValue {
  color: $colorDarkGray;
  font-size: $paragraphFontSize;
  font-style: italic;
}

// Paragraph ────────────────────────────────────────────────

.paragraph {
  color: $colorDarkestGray;
  font-size: $smallFontSize;
  line-height: 1.5;
  margin: 0;
}
